/**
 * Dosya İşlemleri Servisi
 * API'den dosya indirme ve yönetme işlemlerini gerçekleştirir
 */

const rawApiBaseUrl =
  process.env.REACT_APP_FILE_API_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_API_URL ||
  "https://webapi.mngairlines.com";

const normalizeFileBaseUrl = (baseUrl) => {
  if (!baseUrl || typeof baseUrl !== "string") {
    return "https://webapi.mngairlines.com";
  }

  let normalized = baseUrl.trim();

  if (!normalized) {
    return "https://webapi.mngairlines.com";
  }

  if (
    !normalized.startsWith("http://") &&
    !normalized.startsWith("https://") &&
    !normalized.startsWith("/")
  ) {
    normalized = `https://${normalized}`;
  }

  normalized = normalized.replace(/\/+$/, "");
  normalized = normalized.replace(/\/api$/i, "");

  return normalized;
};

const FILE_BASE_URL = normalizeFileBaseUrl(rawApiBaseUrl);

/**
 * Dosya adından MIME type'ı otomatik olarak çıkartır
 * @param {string} fileName - Dosya adı
 * @returns {string} - MIME type (örn: "jpg", "png")
 */
export const getMimeTypeFromFileName = (fileName) => {
    if (!fileName || typeof fileName !== "string") return "";
    return fileName.split(".").pop().toLowerCase();
};

/**
 * Belirtilen dosyayı indirmek için GET isteği gönderir
 * @param {string} fileName - İndirilecek dosyanın adı (örn: "28_01_2026_11_42_05_227134.jpg")
 * @returns {Promise<Blob>} - Dosya blob'u
 */
export const getFile = async (fileName) => {
    if (!fileName || typeof fileName !== "string") {
        console.warn("FileService - getFile: Geçersiz fileName", fileName);
        return null;
    }

    try {
        const mimeType = getMimeTypeFromFileName(fileName);
        const queryParams = new URLSearchParams({
            fileName: fileName,
            mimeType: mimeType,
        });

        const response = await fetch(
            `${FILE_BASE_URL}/file/get?${queryParams.toString()}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/octet-stream",
                },
            }
        );

        if (!response.ok) {
            throw new Error(`Dosya indirilemedi: ${response.statusText}`);
        }

        return await response.blob();
    } catch (error) {
        console.error("FileService - getFile hatası:", error);
        throw error;
    }
};

/**
 * Dosya URL'si oluşturur
 * @param {string} fileName - Dosyanın adı
 * @returns {string} - Tam dosya URL'si
 */
export const getFileUrl = (fileName) => {
    if (!fileName || typeof fileName !== "string") return "";

    const mimeType = getMimeTypeFromFileName(fileName);
    const queryParams = new URLSearchParams({
        fileName: fileName,
        mimeType: mimeType,
    });

    return `${FILE_BASE_URL}/file/get?${queryParams.toString()}`;
};

/**
 * Blob'dan data URL oluşturur (görüntü önizlemesi için)
 * @param {Blob} blob - Dosya blob'u
 * @returns {Promise<string>} - Data URL
 */
export const blobToDataUrl = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * Dinamik olarak görüntü URL'si oluşturur
 * @param {string} fileName - Görüntünün dosya adı
 * @returns {string} - Görüntü URL'si
 */
export const getImageUrl = (fileName) => {
    return getFileUrl(fileName);
};

/**
 * Dosyayı indirip data URL'ye dönüştürür (Canvas/img için)
 * @param {string} fileName - Dosyanın adı
 * @returns {Promise<string>} - Data URL
 */
export const getFileAsDataUrl = async (fileName) => {
    try {
        const blob = await getFile(fileName);
        return await blobToDataUrl(blob);
    } catch (error) {
        console.error("FileService - getFileAsDataUrl hatası:", error);
        throw error;
    }
};

export default {
    getMimeTypeFromFileName,
    getFile,
    getFileUrl,
    blobToDataUrl,
    getImageUrl,
    getFileAsDataUrl,
};
